<template>
  <div class="bid-actions d-flex justify-space-between">
    <v-btn
      variant="text"
      class="bid-actions__cancel"
      :disabled="isLoading"
      @click="closeModal"
      >Cancel</v-btn
    >
    <div class="d-flex align-center gap-3">
      <bid-view-save-as-draft
        v-if="isProposal && isDraftButtonNeed"
        :disabled="isLoading || isDisabledForm || isAuctionInactive"
      />
      <v-btn
        v-if="proposalId"
        variant="flat"
        :disabled="isLoading || isDisabledForm || isAuctionInactive"
        :loading="isWithdrawLoading"
        class="bid-actions__withdraw"
        @click="onDeleteProposal"
        >Withdraw</v-btn
      >
      <v-btn
        variant="flat"
        :disabled="isDisabledForm || isAuctionInactive"
        :loading="isLoading"
        class="bid-actions__submit"
        @click="onSubmit"
        >{{ getButtonValue }}</v-btn
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import useBid from '@/composables/useBid';
import BidViewSaveAsDraft from './BidViewSaveAsDraft.vue';
import { withDefaults, defineProps, computed } from 'vue';
import { useStore } from 'vuex';
import { IAsset } from '@/types/store';
import { RAW_ASSETS_STATUSES } from '@/constants/statuses';

// #interfaces
interface IProps {
  isDisabledForm: boolean;
}

// #props
withDefaults(defineProps<IProps>(), {
  isDisabledForm: false
});

// #constants
const { getters, commit } = useStore();
const {
  onUpdateProposal,
  onDeleteProposal,
  onCreateProposal,
  onUpdateBid,
  closeModal,
  isLoading,
  isWithdrawLoading,
  proposalId,
  isProposal,
  proposalStatus
} = useBid();

// #computed
const asset = computed<IAsset>(() => {
  return getters['getAsset'];
});
const getStatus = computed<string>(() => {
  const { status } = asset.value ?? {};
  return status;
});
const isAuctionCancelled = computed<boolean>(() => {
  return getStatus.value === RAW_ASSETS_STATUSES.CANCELLED;
});
const isAuctionCompleted = computed<boolean>(() => {
  return getStatus.value === RAW_ASSETS_STATUSES.COMPLETED;
});
const isPaused = computed<boolean>(() => {
  // Don't show paused if auction is cancelled or completed
  if (isAuctionCancelled.value || isAuctionCompleted.value) {
    return false;
  }
  const { auction } = asset.value ?? {};
  return !!(auction as any)?.isPaused;
});
const isAuctionInactive = computed<boolean>(() => {
  return isPaused.value || isAuctionCancelled.value || isAuctionCompleted.value;
});
const getButtonValue = computed(() => {
  if (proposalStatus.value === 'draft') {
    return 'Submit';
  }
  return proposalId.value ? 'Update' : 'Submit';
});
const isDraftButtonNeed = computed<boolean>(() => {
  return proposalStatus.value === 'draft' || !proposalStatus.value;
});
const getPhaseValues = computed<(string | number)[]>(() => {
  return getters['getPhaseValues'];
});

// #methods
const validatePhaseValues = (): boolean => {
  const phaseValues = getPhaseValues.value;

  if (!phaseValues || phaseValues.length === 0) {
    return false;
  }

  // Check if all phase values are greater than 0
  const allValid = phaseValues.every((value) => {
    const numValue = Number(value);
    return numValue > 0 && !isNaN(numValue);
  });

  return allValid;
};

const onSubmit = () => {
  // Validate phase values before submission
  if (!validatePhaseValues()) {
    commit('errorNotification', {
      text: 'Invalid bid values',
      subtext:
        'All phase amounts must be greater than $0. Please review and update your bid values.',
      closable: true
    });
    return;
  }

  if (isProposal.value) {
    return proposalId.value
      ? onUpdateProposal('initial')
      : onCreateProposal('initial');
  }
  onUpdateBid();
};
</script>

<style scoped lang="scss">
@import '@/assets/style/_colors.scss';

.bid-actions {
  &__cancel {
    color: #64748b !important;
    font-weight: 500;

    &:hover {
      color: #475569 !important;
      background: rgba(100, 116, 139, 0.08) !important;
    }
  }

  &__withdraw {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
    color: $color-ui-white !important;
    font-weight: 600;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.25);
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35);
    }

    &:disabled {
      opacity: 0.6;
    }
  }

  &__submit {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
    color: $color-ui-white !important;
    font-weight: 600;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.25);
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35);
    }

    &:disabled {
      opacity: 0.6;
    }
  }
}

.gap-3 {
  gap: 12px;
}
</style>
